import { useContext, useEffect, useRef } from "react"

import { InteractionStatus } from "@azure/msal-browser"
import { useMsal } from "@azure/msal-react"
import { env } from "env"

import { AuthContext } from "context/AuthContext"
import { ClientContext } from "context/ClientContext"
import { useErrorModal } from "context/ErrorContext"

import { AppRoutes } from "utilities/constants"

export const LogoutPage = () => {
  const { unsetTokens } = useContext(AuthContext)
  const { clearError } = useErrorModal()
  const { instance, inProgress } = useMsal()
  const { client } = useContext(ClientContext)
  const logoutInProgressRef = useRef(false)

  useEffect(() => {
    // Wait for MSAL instance and for any pending interactions to complete
    if (!instance) {
      return
    }

    // Don't attempt logout while MSAL is processing a redirect
    if (inProgress !== InteractionStatus.None) {
      return
    }

    // Prevent double execution in React Strict Mode
    if (logoutInProgressRef.current) {
      return
    }

    const performLogout = async () => {
      logoutInProgressRef.current = true

      // Step 1: Clear React state
      unsetTokens()
      clearError()

      // Step 2: Build redirect URI for after B2C logout
      const loggedOutPath = `/${AppRoutes.loggedOut}`
      const postLogoutRedirectUri = env.DEV
        ? `${window.location.origin}${loggedOutPath}`
        : `${client?.redirectUrl || window.location.origin}${loggedOutPath}`

      // Step 3: Get current accounts
      const accounts = instance.getAllAccounts()
      const account = accounts.length > 0 ? accounts[0] : undefined

      if (account) {
        // Use logoutRedirect - this properly clears MSAL cache AND
        // redirects to B2C to end the session there
        try {
          await instance.logoutRedirect({
            postLogoutRedirectUri,
            account,
          })
          // Note: This redirect will happen, code after this won't execute
        } catch (e) {
          console.error("[Logout] logoutRedirect failed:", e)
          // If logoutRedirect fails, clear cache manually and redirect
          await instance.clearCache()
          window.location.href = postLogoutRedirectUri
        }
      } else {
        // No account found - clear any stale cache and redirect
        await instance.clearCache()
        window.location.href = postLogoutRedirectUri
      }
    }

    performLogout()
  }, [unsetTokens, clearError, instance, inProgress, client])

  return null
}
